/**
 * Google Maps JS injects Roboto without font-display, which Lighthouse flags
 * (~40ms invisible text). Rewrite those stylesheet URLs to include display=swap
 * before they are requested.
 *
 * Safe to call multiple times; patches document.head once.
 */

const APPLIED_FLAG = 'data-maps-roboto-font-display';

function isRobotoFontsStylesheet(href) {
  if (!href || typeof href !== 'string') return false;
  if (!href.includes('fonts.googleapis.com/css')) return false;
  return /family=Roboto|Roboto:/i.test(href);
}

export function withFontDisplaySwap(href) {
  if (!isRobotoFontsStylesheet(href)) return href;
  if (/[?&]display=/.test(href)) return href;
  return `${href}${href.includes('?') ? '&' : '?'}display=swap`;
}

function patchFontLink(node) {
  if (!node || node.nodeName !== 'LINK') return;
  const href = node.getAttribute('href') || node.href;
  const next = withFontDisplaySwap(href);
  if (next && next !== href) {
    node.setAttribute('href', next);
  }
}

export default function ensureMapsRobotoFontDisplay() {
  if (typeof document === 'undefined') return;
  if (document.documentElement.getAttribute(APPLIED_FLAG) === 'true') return;
  document.documentElement.setAttribute(APPLIED_FLAG, 'true');

  const { head } = document;
  if (!head) return;

  // Patch any Roboto links Maps already injected.
  head.querySelectorAll('link[href*="fonts.googleapis.com"]').forEach(patchFontLink);

  const originalInsertBefore = head.insertBefore.bind(head);
  head.insertBefore = (newNode, referenceNode) => {
    patchFontLink(newNode);
    return originalInsertBefore(newNode, referenceNode);
  };

  const originalAppendChild = head.appendChild.bind(head);
  head.appendChild = (newNode) => {
    patchFontLink(newNode);
    return originalAppendChild(newNode);
  };

  // Maps sometimes sets href after inserting an empty link.
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => patchFontLink(node));
      } else if (mutation.type === 'attributes') {
        patchFontLink(mutation.target);
      }
    });
  });
  observer.observe(head, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href'],
  });
}
